/**
 * Scene Config Panel
 * Sprint 19: Custom Template Creation
 * 
 * Individual scene configuration: title, duration, layout, background, script
 */

import React, { useMemo } from 'react';
import { Input, InputNumber, Tabs, Space, Tag } from 'antd';
import { 
  EditOutlined, 
  ClockCircleOutlined, 
  LayoutOutlined,
  BgColorsOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { TemplateScene, TemplateScript, TemplateCategory, SceneTagType, SceneBackground } from '../../../../types/template';
import { LayoutSelectorPanel } from './LayoutSelectorPanel';
import { BackgroundPicker } from './BackgroundPicker';
import { ScriptEditorPanel } from './ScriptEditorPanel';
import { SceneTagSelector } from '../scenes/SceneCard/SceneTagSelector';
import './SceneConfigPanel.scss';

interface SceneConfigPanelProps {
  scene: TemplateScene;
  script: TemplateScript | null;
  sceneIndex: number;
  category: TemplateCategory;
  aspectRatio?: '16:9' | '9:16' | '1:1';
  onUpdateScene: (updates: Partial<TemplateScene>) => void;
  onUpdateScript: (content: string) => void;
  onUpdateAspectRatio?: (ratio: '16:9' | '9:16' | '1:1') => void;
}

export const SceneConfigPanel: React.FC<SceneConfigPanelProps> = ({
  scene,
  script,
  aspectRatio = '16:9',
  sceneIndex,
  category,
  onUpdateScene,
  onUpdateScript,
  onUpdateAspectRatio
}) => {
  const wordCount = useMemo(() => {
    if (!script?.content) return 0;
    return script.content.trim().split(/\s+/).filter(Boolean).length;
  }, [script?.content]);

  const tabItems = [
    {
      key: 'general',
      label: (
        <span>
          <EditOutlined /> General
        </span>
      ),
      children: (
        <div className="config-section">
          <div className="form-group">
            <label>Scene Title</label>
            <Input
              value={scene.title}
              onChange={(e) => onUpdateScene({ title: e.target.value })}
              placeholder="Enter scene title..."
            />
          </div>
          
          <div className="form-group">
            <label>
              <ClockCircleOutlined /> Duration (seconds)
            </label>
            <InputNumber
              value={scene.suggestedDuration}
              onChange={(value) => onUpdateScene({ suggestedDuration: value || 60 })}
              min={5}
              max={600}
              style={{ width: '100%' }}
            />
          </div>
          
          <div className="form-group">
            <label>Scene Tags</label>
            <SceneTagSelector
              selectedTags={(scene.tags as SceneTagType[]) || []}
              onChange={(tags) => onUpdateScene({ tags })}
              maxTags={3}
            />
          </div>
          
          <div className="form-group">
            <label>Notes / Tips</label>
            <Input.TextArea
              value={scene.notes || ''}
              onChange={(e) => onUpdateScene({ notes: e.target.value })}
              placeholder="Add tips or guidance for recording this scene..."
              rows={3}
            />
          </div>
        </div>
      )
    },
    {
      key: 'layout',
      label: (
        <span>
          <LayoutOutlined /> Layout
        </span>
      ),
      children: (
        <LayoutSelectorPanel
          selectedPreset={scene.layoutPreset}
          onSelectPreset={(preset, presetRatio) => {
            onUpdateScene({ layoutPreset: preset });
            if (onUpdateAspectRatio && presetRatio !== aspectRatio) {
              onUpdateAspectRatio(presetRatio);
            }
          }}
          aspectRatio={aspectRatio}
        />
      )
    },
    {
      key: 'background',
      label: (
        <span>
          <BgColorsOutlined /> Background
        </span>
      ),
      children: (
        <BackgroundPicker
          background={scene.background as SceneBackground}
          onChange={(background) => onUpdateScene({ background })}
        />
      )
    },
    {
      key: 'script',
      label: (
        <span>
          <FileTextOutlined /> Script
          {wordCount > 0 && <Tag className="word-count-tag">{wordCount}</Tag>}
        </span>
      ),
      children: script ? (
        <ScriptEditorPanel
          script={script}
          onChange={onUpdateScript}
          category={category}
          sceneTitle={scene.title}
          suggestedDuration={scene.suggestedDuration}
        />
      ) : (
        <div className="no-script">
          <p>No script linked to this scene.</p>
        </div>
      )
    }
  ];

  return (
    <div className="scene-config-panel">
      <div className="panel-header">
        <h3>
          Scene {sceneIndex + 1}: {scene.title}
        </h3>
        <Space>
          <Tag icon={<ClockCircleOutlined />}>{scene.suggestedDuration}s</Tag>
          <Tag color="blue">{scene.layoutPreset}</Tag>
        </Space>
      </div>
      
      <Tabs
        items={tabItems}
        defaultActiveKey="general"
        className="config-tabs"
      />
    </div>
  );
};

export default SceneConfigPanel;
